import { ICommand } from '../../../core/commands/ICommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { createEdgeId, createNodeId, type EdgeAddition } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';

export interface CreateSequenceFlowParams {
  from: string;
  to: string;
  fromAnchor?: 'top' | 'right' | 'bottom' | 'left';
  toAnchor?: 'top' | 'right' | 'bottom' | 'left';
  fromOffset?: number;
  toOffset?: number;
  routingType?: 'straight' | 'orthogonal' | 'orthogonalRounded' | 'bezier';
  /** Default branch condition shown on an outgoing decision connector. */
  label?: string;
}

export class CreateSequenceFlowCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Sequence Flow';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateSequenceFlowParams;
  private inner?: CreateEdgeCommand;
  private scenarioEdgeId: ReturnType<typeof createEdgeId>;

  constructor(params: CreateSequenceFlowParams) {
    this.id = `create-sequence-flow-${Date.now()}`;
    this.timestamp = Date.now();
    this.params = params;
    this.description = 'Create a sequence flow connector';
    this.scenarioEdgeId = createEdgeId(`scenario-${this.id}`);
  }

  execute(): void {
    const attrs: Record<string, unknown> = {
      routingType: this.params.routingType ?? 'orthogonalRounded',
    };
    if (this.params.label) {
      attrs.label = this.params.label;
    }

    this.inner = new CreateEdgeCommand({
      type: 'sequence_flow',
      from: this.params.from,
      to: this.params.to,
      category: 'flowchart',
      fromAnchor: this.params.fromAnchor as any,
      toAnchor: this.params.toAnchor as any,
      fromOffset: this.params.fromOffset as any,
      toOffset: this.params.toOffset as any,
      attrs: attrs as any,
    } as any);

    this.inner.execute();
  }

  undo(): void {
    this.inner?.undo();
  }

  redo(): void {
    this.inner?.redo();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const addition: EdgeAddition = {
      id: this.scenarioEdgeId,
      type: 'sequence_flow',
      from: createNodeId(this.params.from),
      to: createNodeId(this.params.to),
      props: {
        fromAnchor: this.params.fromAnchor,
        toAnchor: this.params.toAnchor,
        fromOffset: this.params.fromOffset,
        toOffset: this.params.toOffset,
        routingType: this.params.routingType ?? 'orthogonalRounded',
        ...(this.params.label ? { label: this.params.label } : {}),
      },
    };

    return { edgeAdditions: [addition] };
  }

  getUndoDelta(): CommandDeltaContribution {
    return { edgeRemovals: [{ edgeId: this.scenarioEdgeId }] };
  }
}
